import React, { FC, memo, useMemo } from "react";
import { FeatureDc } from "@evergis/api";

import { InnerContainerWrapper } from "../styled";
import { getContainerTemplate } from "../utils/getContainerTemplate";
import { getDataFromRelatedFeatures } from "../utils/getDataFromRelatedFeatures";
import { renderElement } from "../utils/renderElement";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { useFeatureFilters } from "../hooks/useFeatureFilters";
import { useRelatedDataSourceAttributes } from "../hooks/useRelatedDataSourceAttributes";
import { ContainerProps } from "../types";

export const DataSourceInnerContainer: FC<Omit<ContainerProps, "renderElement"> & { feature: FeatureDc }> = memo(
  ({ feature, config, elementConfig, maxValue, type, index }) => {
    const { page, expandedContainers, selectedTabId, setSelectedTabId, dataSources } = useWidgetContext(type);
    const { attributes, layerInfo } = useRelatedDataSourceAttributes({ config, elementConfig, dataSources, feature });

    const { options, children } = elementConfig || {};
    const { innerTemplateName, relatedDataSource, filterName, column } = options || {};

    const aliasElement = useMemo(() => children?.find(({ id }) => id === "alias"), [children]);
    const valueElement = useMemo(() => children?.find(({ id }) => id === "value"), [children]);

    const aliasAttribute = useMemo(
      () => (aliasElement ? attributes?.find(({ name }) => name === aliasElement.attributeName) : null),
      [aliasElement, attributes],
    );

    const value = aliasAttribute?.value as string;

    const InnerContainer = getContainerTemplate(innerTemplateName);
    const data = useMemo(
      () =>
        getDataFromRelatedFeatures({
          config: elementConfig,
          filters: config?.filters,
          relatedConfig: {
            dataSourceName: relatedDataSource,
            attributeName: valueElement?.attributeName as string,
            attributeTitle: aliasElement?.attributeName as string,
            filterName,
          },
          dataSource: dataSources?.find(({ name }) => name === relatedDataSource),
          layerInfo,
        }),
      [
        aliasElement?.attributeName,
        config?.filters,
        dataSources,
        elementConfig,
        filterName,
        layerInfo,
        relatedDataSource,
        valueElement?.attributeName,
      ],
    );
    const { hasAnyFilter, isFiltered, onFilter } = useFeatureFilters(type, filterName, data);

    const render = useMemo(
      () =>
        renderElement({
          config,
          elementConfig,
          attributes,
          layerInfo,
          expandedContainers,
          selectedTabId,
          setSelectedTabId,
          page,
          type,
        }),
      [config, elementConfig, attributes, layerInfo, expandedContainers, selectedTabId, setSelectedTabId, page, type],
    );

    return (
      <InnerContainerWrapper
        hasAnyFilter={hasAnyFilter}
        isFiltered={isFiltered(value)}
        column={column === undefined || column}
        onClick={filterName ? () => onFilter(value) : undefined}
      >
        <InnerContainer
          layerInfo={layerInfo}
          config={config}
          elementConfig={elementConfig}
          attributes={attributes}
          maxValue={maxValue}
          type={type}
          index={index}
          renderElement={render}
        />
      </InnerContainerWrapper>
    );
  },
);
